import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";

const clips = [
  {
    src: "/assets/videos/curb-clip-2.mp4",
    poster: "/assets/videos/poster-2.jpg",
    caption: "the victory lap. volume up.",
    ratio: "1480 / 960",
    wide: true,
  },
  {
    src: "/assets/videos/curb-clip-3.mp4",
    poster: "/assets/videos/poster-3.jpg",
    caption: "HODL. NEVER SELLING.",
    ratio: "630 / 960",
  },
  {
    src: "/assets/videos/curb-clip-4.mp4",
    poster: "/assets/videos/poster-4.jpg",
    caption: "$SOL can run. it can't hide.",
    ratio: "630 / 960",
  },
  {
    src: "/assets/videos/curb-clip-1.mp4",
    poster: "/assets/videos/poster-1.jpg",
    caption: "exhaling pure conviction",
    ratio: "1056 / 864",
    wide: true,
  },
];

const VideoCard = ({ c, i }) => {
  const vref = useRef(null);
  const wrapRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [srcActive, setSrcActive] = useState(false);

  const toggle = () => {
    const v = vref.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  };

  // Lazy-load: only attach src when the card first scrolls near the viewport,
  // and pause playback when it scrolls out. Preserves user's explicit play
  // state via `data-userplay` so we only resume videos the user chose to play.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof IntersectionObserver === "undefined") {
      setSrcActive(true); // fallback for older browsers
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setSrcActive(true);
          const v = vref.current;
          if (!v) continue;
          if (!e.isIntersecting) {
            if (!v.paused) {
              v.dataset.userplay = "1";
              v.pause();
            }
          } else if (v.dataset.userplay === "1") {
            v.dataset.userplay = "";
            v.play().catch(() => {});
          }
        }
      },
      { threshold: 0.1, rootMargin: "300px 0px" }
    );
    io.observe(wrap);
    return () => io.disconnect();
  }, []);

  return (
    <motion.figure
      data-testid={`video-clip-${i + 1}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (i % 3) * 0.12 }}
      className={`relative overflow-hidden rounded-2xl border border-[#9945FF]/30 bg-[#090712] flex flex-col ${
        c.wide ? "md:col-span-2" : ""
      }`}
    >
      <div
        ref={wrapRef}
        className="relative w-full cursor-pointer group/vid"
        style={{ aspectRatio: c.ratio }}
        onClick={toggle}
        data-testid={`video-toggle-${i + 1}`}
      >
        <video
          ref={vref}
          src={srcActive ? c.src : undefined}
          poster={srcActive ? c.poster : undefined}
          playsInline
          loop
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => {
            const v = e.target;
            if (v.duration) setProgress(v.currentTime / v.duration);
          }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
            playing ? "opacity-0 group-hover/vid:opacity-100 bg-black/10" : "opacity-100 bg-black/25"
          }`}
        >
          <span
            className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full flex items-center justify-center transition-transform duration-200 group-hover/vid:scale-110"
            style={{
              background: "linear-gradient(135deg, #14F195 0%, #5497D5 55%, #9945FF 100%)",
              boxShadow: "0 0 34px rgba(20,241,149,0.55), 0 4px 20px rgba(0,0,0,0.5)",
            }}
            data-testid={`video-play-btn-${i + 1}`}
          >
            {playing ? (
              <Pause size={26} className="text-[#050409] fill-[#050409]" />
            ) : (
              <Play size={26} className="text-[#050409] fill-[#050409] ml-1" />
            )}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
          <div
            className="h-full transition-[width] duration-200 ease-linear"
            style={{ width: `${progress * 100}%`, background: "linear-gradient(90deg, #14F195, #9945FF)" }}
          />
        </div>
      </div>
      <figcaption className="px-4 py-3 border-t border-[#9945FF]/20 shrink-0 bg-[#090712]">
        <span className="font-marker text-[#14F195] text-sm sm:text-base">{c.caption}</span>
      </figcaption>
    </motion.figure>
  );
};

export const VideoGallery = () => (
  <section id="clips" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      className="mb-12 flex items-end justify-between flex-wrap gap-4"
    >
      <div>
        <p className="font-bebas tracking-[0.35em] text-[#14F195] text-lg">NOW SHOWING</p>
        <h2
          data-testid="video-gallery-heading"
          className="font-marker text-4xl sm:text-5xl gradient-sol text-glow-green mt-2 -rotate-1 inline-block"
        >
          BULL CINEMA
        </h2>
      </div>
      <p className="font-marker text-zinc-500 rotate-1">press play. feel bullish.</p>
    </motion.div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 items-start">
      {clips.map((c, i) => (
        <VideoCard key={c.src} c={c} i={i} />
      ))}
    </div>
  </section>
);
